import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Directions as DirectionsIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { GooglePlaceWithDistance } from "../../../../../types/googlePlaces";
import { Shop } from "../../../../../types/shop";
import type { ShopDetailsDialogData } from "./ShopDetailsDialog";

// Union type for both trusted and Google shop data
type ShopData =
  | {
      type: "trusted";
      shop: {
        id: number;
        name: string;
        address: string;
        phoneNumber?: string | null;
        workingHours?: string | null;
        description?: string | null;
        website?: string | null;
      };
      aerialDistance: number;
      distanceText: string;
      drivingDistance?: string;
      drivingDuration?: string;
    }
  | {
      type: "google";
      shop: GooglePlaceWithDistance;
      convertedShop?: Shop;
    };

interface ShopCardProps {
  data: ShopData;
  onCenterShop?: (shop: any) => void;
  onShowDetails?: (shop: Shop) => Promise<Shop | void> | Shop | void;
  onShowDetailsDialog?: (details: ShopDetailsDialogData) => void;
  onAddShop?: (shop: Shop, preference: number) => void;
  shortenTime: (timeStr: string) => string;
  isFetchingDetails?: boolean;
}

export const ShopCard: React.FC<ShopCardProps> = ({
  data,
  onCenterShop,
  onShowDetails,
  onShowDetailsDialog,
  onAddShop,
  shortenTime,
  isFetchingDetails = false,
}) => {
  const isTrusted = data.type === "trusted";
  const isGoogle = data.type === "google";

  // Get shop info based on type
  const shopName = isTrusted ? data.shop.name : data.shop.name;

  // Get distance info based on type
  // For trusted shops: show driving distance if available, otherwise show aerial distance
  const hasDrivingDistance = isTrusted
    ? data.drivingDistance && data.drivingDistance !== "N/A"
    : !!(
        data.shop.roadDistance ||
        (data.convertedShop?.distance && data.convertedShop?.duration)
      );

  // For trusted shops: prefer driving distance, fallback to aerial distance
  const distanceText = isTrusted
    ? data.drivingDistance && data.drivingDistance !== "N/A"
      ? data.drivingDistance
      : data.distanceText || "N/A" // Fallback to aerial distance, always show something
    : data.shop.roadDistance?.distance.text ||
      data.convertedShop?.distance ||
      null;

  const durationText = isTrusted
    ? data.drivingDuration && data.drivingDuration !== "N/A"
      ? data.drivingDuration
      : null // No duration for aerial distance
    : data.shop.roadDistance?.duration.text ||
      data.convertedShop?.duration ||
      null;

  // Show distance info if we have either driving or aerial distance
  const hasDistanceInfo = isTrusted
    ? !!(distanceText && distanceText !== "N/A")
    : hasDrivingDistance;

  const buildDetailsData = (detailedShop?: Shop): ShopDetailsDialogData => {
    if (isTrusted) {
      return {
        type: "trusted",
        name: data.shop.name,
        address: data.shop.address,
        phoneNumber: data.shop.phoneNumber ?? null,
        workingHours: data.shop.workingHours ?? null,
        description: data.shop.description ?? null,
        website: data.shop.website ?? null,
      };
    }

    const shop = detailedShop ?? data.convertedShop;
    return {
      type: "google",
      name: data.shop.name,
      address: shop?.address || data.shop.vicinity || null,
      phoneNumber:
        shop?.phoneNumber ||
        (data.shop as any).formatted_phone_number ||
        (data.shop as any).international_phone_number ||
        null,
      workingHours: shop?.workingHours || null,
      description: shop?.description || null,
      website: shop?.website || null,
      rating: data.shop.rating ?? shop?.rating,
      userRatingsTotal: data.shop.user_ratings_total ?? shop?.userRatingsTotal,
    };
  };

  const handleShowDetails = async (
    event: React.MouseEvent<HTMLElement>
  ): Promise<void> => {
    event.stopPropagation();

    if (isGoogle && data.convertedShop) {
      const detailedShop = await onShowDetails?.(data.convertedShop);
      onShowDetailsDialog?.(
        buildDetailsData(detailedShop || data.convertedShop)
      );
      return;
    }

    onShowDetailsDialog?.(buildDetailsData());
  };

  const phoneNumber = isGoogle
    ? data.convertedShop?.phoneNumber ||
      (data.shop as any).formatted_phone_number ||
      (data.shop as any).international_phone_number ||
      null
    : data.shop.phoneNumber || null;

  const handleCall = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, "_self");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        px: 0.5,
        py: 0.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        position: "relative",
        cursor: onCenterShop ? "pointer" : "default",
        "&:hover": onCenterShop
          ? {
              bgcolor: "action.hover",
            }
          : {},
      }}
      onClick={() => {
        if (onCenterShop) {
          onCenterShop(data.shop);
        }
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: "0.78rem",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={shopName}
        >
          {shopName}
        </Typography>
        {hasDistanceInfo && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
              minWidth: 0,
            }}
          >
            <DirectionsIcon sx={{ fontSize: "0.75rem", color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: isTrusted ? "warning.main" : "primary.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {distanceText}
            </Typography>
            {isTrusted && durationText && (
              <>
                <ScheduleIcon
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortenTime(durationText || "")}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.125, flexShrink: 0 }}>
        <Tooltip title={phoneNumber ? "Call shop" : "Phone not available"}>
          <span>
            <IconButton
              size="small"
              aria-label="Call shop"
              disabled={!phoneNumber}
              onClick={handleCall}
              sx={{
                width: 24,
                height: 26,
                color: phoneNumber ? "success.main" : "action.disabled",
              }}
            >
              <PhoneIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Shop details">
          <IconButton
            size="small"
            aria-label="Show shop details"
            onClick={handleShowDetails}
            sx={{ width: 24, height: 26, color: "primary.main" }}
          >
            {isGoogle && isFetchingDetails ? (
              <CircularProgress size={14} />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: "0.9rem" }} />
            )}
          </IconButton>
        </Tooltip>

      {isGoogle && onAddShop && data.convertedShop && (
        <>
          <Tooltip title="Add as trusted shop">
            <IconButton
              size="small"
              aria-label="Add as trusted shop"
              onClick={(e) => {
                e.stopPropagation();
                if (data.convertedShop && onAddShop) {
                  onAddShop(data.convertedShop, 0); // 0 = Trusted
                }
              }}
              sx={{
                width: 24,
                height: 26,
                borderRadius: 1,
                backgroundColor: "#FFA726",
                color: "black",
                "&:hover": {
                  backgroundColor: "#FB8C00",
                },
              }}
            >
              <CheckCircleIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add as DNU shop">
            <IconButton
              size="small"
              aria-label="Add as DNU shop"
              onClick={(e) => {
                e.stopPropagation();
                if (data.convertedShop && onAddShop) {
                  onAddShop(data.convertedShop, 1); // 1 = DNU
                }
              }}
              sx={{
                width: 24,
                height: 26,
                borderRadius: 1,
                backgroundColor: "error.main",
                color: "error.contrastText",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
              }}
            >
              <BlockIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </Tooltip>
        </>
      )}
      </Box>
    </Box>
  );
};
